import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { ROLE_TYPES } from '../../../../constants/roleTypes';
import useCVStoreEmployee from '../../../../store/cvStoreEmployee';
import { useAlert } from '../../../../contexts/AlertContext';

// Create and export the context directly with a named export for Fast Refresh compatibility
export const CVContext = createContext();

// Convert to named function for Fast Refresh compatibility
export function useCV() {
  return useContext(CVContext);
}

export const CVProvider = ({ children, employee, cvId, disableEmployeeChange, autoLinkEmployee, readonly = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDataReady, setIsDataReady] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Access cvStoreEmployee methods and state
  const {
    fetchCVById,
    saveCV: storeSaveCV,
    deleteCV: storeDeleteCV,
    createNewCV,
    getError,
    currentCV,
    getIsNewCV,
    updateCurrentCV,
    getCVMetadata,
  } = useCVStoreEmployee();
  
  // Use store's currentCV as the single source of truth
  const cvData = currentCV;
  
  // Get metadata from store
  const cvMetadata = getCVMetadata();
  
  // Combine CV data with metadata for components that need it (like template processing)
  const cvDataWithMetadata = cvData ? {
    ...cvData,
    metadata: cvMetadata
  } : null;
  
  // Common function to load CV data from store
  const loadCVData = async (cvId) => {
    try {
      setIsLoading(true);
      
      // Fetch CV data using store - store will update its currentCV
      const fetchedData = await fetchCVById(cvId);
      
      setIsLoading(false);
      setIsDataReady(true);

      if (fetchedData === null) {
        return false;
      }

      return true;

    } catch (err) {
      console.error('Error fetching CV data');
      setIsLoading(false);
      return false;
    }
  };
  
  // Fetch CV data from the store using the provided ID or test ID
  useEffect(() => {
    const fetchCVData = async () => {
      // If cvId is provided and not null, load that specific CV
      if (cvId) {
        const success = await loadCVData(cvId);
        
        // If loading failed, initialize with employee data
        if (!success) {
          //initializeWithEmployeeData();
          setIsLoading(false);
        }

      } else {
        // If cvId is null, it's a new CV, so just initialize with employee data
        initializeWithEmployeeData();
        setIsLoading(false);
        setIsDataReady(true);
        // Mark as dirty since it's a new CV
        setIsDirty(true);
      }
    };
    
    fetchCVData();
  }, [cvId]);
  
  // Function to reload CV data from the server
  const reloadCV = async () => {
    return await loadCVData(cvData?.id);
  };
  
  // Initialize CV data from provided employee prop or user's employee data
  // This function should only be called for NEW CVs (when cvId is null)
  const initializeWithEmployeeData = () => {
    // If autoLinkEmployee is true, automatically link the CV to the employee
    if (autoLinkEmployee && employee) {
      // Use createNewCV from store for new CVs - store will update its currentCV
      // The createNewCV function already handles clearing existing data
      createNewCV(employee);
    } else {
      createNewCV();
    }
    
    setIsDataReady(true);
  };
  
  // Update personal details
  const updatePersonalDetails = (details) => {
    if (readonly) {
      return;
    }
    updateCurrentCV({
      fullName: details.employee?.fullName || '',
      country: details.country,
      city: details.city,
      position: details.position,
      grade: details.grade,
      roles: details.roles,
      customClassification: details.customClassification,
      useCustomClassification: details.useCustomClassification,
      employee: {
        ...cvData?.employee,
        id: details.employee?.id,
        azureId: details.employee?.azureId,
        fullName: details.employee?.fullName
      }
    });

    setIsDirty(true);
  };
  
  // Update professional summary
  const updateProfessionalSummary = (summary) => {
    if (readonly) {
      return;
    }
    updateCurrentCV({
      professionalSummary: summary
    });
    setIsDirty(true);
  };
  
  // Update employment history
  const updateEmploymentHistory = (jobs) => {
    if (readonly) {
      return;
    }
    updateCurrentCV({
      employmentHistory: jobs
    });
    setIsDirty(true);
  };
  
  // Update education
  const updateEducation = (education) => {
    if (readonly) {
      return;
    }
    updateCurrentCV({
      education
    });
    setIsDirty(true);
  };
  
  // Update courses
  const updateCourses = (courses) => {
    if (readonly) {
      return;
    }
    updateCurrentCV({
      courses
    });
    setIsDirty(true);
  };
  
  // Update certifications
  const updateCertifications = (certifications) => {
    if (readonly) {
      return;
    }
    updateCurrentCV({
      certifications
    });
    setIsDirty(true);
  };
  
  // Update languages
  const updateLanguages = (languages) => {
    if (readonly) {
      return;
    }
    updateCurrentCV({
      languages
    });
    setIsDirty(true);
  };

  // Update online profiles
  const updateOnlineProfiles = (onlineProfiles) => {
    if (readonly) {
      return;
    }
    updateCurrentCV({
      onlineProfiles
    });
    setIsDirty(true);
  };

  // Update skills
  const updateSkills = (skills) => {
    if (readonly) {
      return;
    }
    updateCurrentCV({
      skills
    });
    setIsDirty(true);
  };
  
  // Update roles
  const updateRoles = (roles) => {
    if (readonly) {
      return;
    }
    updateCurrentCV({
      roles
    });
    setIsDirty(true);
  };
  
  // Export CV data as JSON
  const exportCV = () => {
    return JSON.stringify(cvData, null, 2);
  };
  
  // Import CV data from JSON
  const importCV = (jsonData) => {
    try {
      const data = JSON.parse(jsonData);
      updateCurrentCV(data);
      setIsDirty(true);
      return true;
    } catch (error) {
      console.error('Error importing CV data');
      return false;
    }
  };
  
  // Save CV data using the store
  const saveCV = async () => {
    if (readonly || !cvData) {
      return false;
    }
    try {
      // Check if we're creating a new CV (no ID before)
      const isNewCV = getIsNewCV();
      
      // Call the store method to save the CV data
      const result = await storeSaveCV(cvData);
      
      if (result && result.id) {
        
        // Reset the dirty flag after successful save
        setIsDirty(false);
        
        // If this was a new CV and we're on the "new" route, redirect to the CV with its new ID
        if (isNewCV && location.pathname.includes('/cv-repository/new')) {
          // Check current path to determine correct redirect route
          if (location.pathname.includes('/my-cvs/')) {
            navigate(`/cv-repository/my-cvs/${result.id}`, { replace: true });
          } else {
            navigate(`/cv-repository/${result.id}`, { replace: true });
          }
        }
        
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error saving CV data');
      return false;
    }
  };

  // Delete CV data using the store
  const deleteCV = async () => {
    if (readonly || !cvData || !cvData.id) {
      return false;
    }
    try {
      
      // Call the store method to delete the CV data
      const result = await storeDeleteCV(cvData.id);
      
      if (result) {
        
        // Navigate back to the appropriate list after successful deletion
        // Check current path to determine where to navigate
        if (location.pathname.includes('/my-cvs/')) {
          navigate('/cv-repository/my-cvs', { replace: true });
        } else if (location.pathname.includes('/cv-repository/')) {
          navigate('/cv-repository', { replace: true });
        } else {
          navigate(-1);
        }
        
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error deleting CV data');
      return false;
    }
  };
  
  // Context value
  const contextValue = {
    cvData,
    cvDataWithMetadata,
    isDataReady,
    isDirty,
    isLoading,
    disableEmployeeChange,
    readonly,
    updatePersonalDetails,
    updateProfessionalSummary,
    updateEmploymentHistory,
    updateEducation,
    updateCourses,
    updateCertifications,
    updateLanguages,
    updateOnlineProfiles,
    updateSkills,
    updateRoles,
    exportCV,
    importCV,
    saveCV,
    deleteCV,
    reloadCV,
    getError,
    getCVMetadata,
  };
  
  return (
    <CVContext.Provider value={contextValue}>
      {children}
    </CVContext.Provider>
  );
}; 